import { FC, PropsWithChildren, useMemo } from 'react'
import { AccountInfo } from '@azure/msal-browser'
import { useAccount, useMsal } from '@azure/msal-react'
import { MSAL_ROLE_ID_EV_EMP_UI_DEVELOPER, MSAL_ROLE_ID_EV_EMP_UI_READER } from '@/constants'
import { UserRole } from '@/application/auth/types/userRole'
import { AuthContext, AuthContextValue } from '@/application/auth/contexts/AuthContext'

type AccountWithGroups = AccountInfo & {
  idTokenClaims?: {
    groups?: string[]
  }
}

const extractRoles = (account: AccountWithGroups | null): UserRole[] => {
  if (!account?.idTokenClaims?.groups) {
    return []
  }

  const roles: UserRole[] = []
  const roleIds = account.idTokenClaims.groups

  if (roleIds.includes(MSAL_ROLE_ID_EV_EMP_UI_DEVELOPER)) {
    roles.push(UserRole.Developer)
  }
  if (roleIds.includes(MSAL_ROLE_ID_EV_EMP_UI_READER)) {
    roles.push(UserRole.Reader)
  }

  return roles
}

export const AuthContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { accounts, instance } = useMsal()
  const account = useAccount(accounts[0] || null)

  const value: AuthContextValue = useMemo(() => {
    const roles = extractRoles(account)
    const hasRole = (requiredRole: UserRole): boolean => roles.includes(requiredRole)
    const hasAnyRole = (requiredRoles: UserRole[]): boolean => requiredRoles.some((requiredRole) => hasRole(requiredRole))

    return {
      isAuthenticated: !!account,
      name: account?.name || null,
      email: account?.username || null,
      roles,
      hasRole,
      hasAnyRole,
      logout: () => instance.logoutRedirect()
    }
  }, [account, instance])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
